import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import EvaluationGroupCard from './EvaluationGroupCard';
import { getEvaluationGroup, getWorkflowTemplate } from '../services/api';

const EvaluatorsDashboard = () => {
  const { groupId } = useParams();

  const [evaluationGroups, setEvaluationGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stateNameMap, setStateNameMap] = useState({});
  const [workflowTemplate, setWorkflowTemplate] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    const fetchGroup = async () => {
      if (!groupId) return;

      setLoading(true);
      try {
        const groupData = await getEvaluationGroup(groupId);
        setEvaluationGroups([groupData]);

        // Fetch workflow template if workflow_template_id exists
        if (groupData.evaluation_template_id) {
          try {
            const template = await getWorkflowTemplate(groupData.evaluation_template_id);
            setWorkflowTemplate(template);
            // Create a mapping of state_id to state_name
            const mapping = {};
            if (template.states && Array.isArray(template.states)) {
              template.states.forEach(state => {
                mapping[state.id] = state.name;
              });
            }
            setStateNameMap(mapping);
          } catch (workflowError) {
            console.error('Failed to fetch workflow template:', workflowError);
            // Continue without workflow state names
          }
        }
      } catch (error) {
        console.error(error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch Evaluation Group';
        showNotification(errorMessage, 'error');
        setEvaluationGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [groupId]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Edit Evaluation Group
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : evaluationGroups.length > 0 ? (
        evaluationGroups.map((group) => (
          <EvaluationGroupCard
            key={group.id}
            group={group}
            stateNameMap={stateNameMap}
            workflowTemplate={workflowTemplate}
            onSuccess={showNotification}
            onError={(error) => showNotification(error, 'error')}
          />
        ))
      ) : (
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            {groupId ? 'No evaluation group found.' : 'No Group ID provided.'}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvaluatorsDashboard;
